import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

const AuthContext = createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH (Emergent-managed Google Auth playbook).
const EMERGENT_AUTH_BASE = 'https://auth.emergentagent.com';

async function fetchCurrentUser() {
  try {
    const { data } = await api.get('/auth/me');
    return data.user;
  } catch (err) {
    console.error('auth/me failed', err);
    return null;
  }
}

async function fetchTwitchLoginUrl() {
  try {
    const { data } = await api.get('/auth/twitch/login');
    return data.url;
  } catch (err) {
    console.error('twitch login url failed', err);
    return null;
  }
}

async function postLogout() {
  try {
    await api.post('/auth/logout');
  } catch (err) {
    console.error('logout failed', err);
  }
}

async function exchangeGoogleSession(sessionId) {
  const { data } = await api.post('/auth/google/session', {
    session_id: sessionId
  });
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const googleExchanged = useRef(false);

  // Handle Emergent Google Auth callback (#session_id=...) BEFORE
  // fetching /auth/me so we don't trigger a 401 on a stale session.
  useEffect(() => {
    let active = true;

    const run = async () => {
      const hash = window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      if (match && !googleExchanged.current) {
        googleExchanged.current = true;
        const sessionId = decodeURIComponent(match[1]);
        // Clear the hash from the URL immediately so refreshes don't
        // re-trigger this flow.
        try {
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search
          );
        } catch (_) {
          /* ignore */
        }
        try {
          await exchangeGoogleSession(sessionId);
          toast({
            title: 'Ingelogd met Google',
            description: 'Welkom! Je kunt nu stemmen en suggesties indienen.'
          });
        } catch (err) {
          console.error('google session exchange failed', err);
          toast({
            title: 'Google login mislukt',
            description: 'Probeer het opnieuw of gebruik Twitch.',
            variant: 'destructive'
          });
        }
      }

      const u = await fetchCurrentUser();
      if (!active) return;
      setUser(u);
      setLoading(false);
    };

    run();
    return () => {
      active = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const u = await fetchCurrentUser();
    setUser(u);
    setLoading(false);
  }, []);

  const openLogin = useCallback(() => setDialogOpen(true), []);
  const closeLogin = useCallback(() => setDialogOpen(false), []);

  const loginTwitch = useCallback(async () => {
    setDialogOpen(false);
    const url = await fetchTwitchLoginUrl();
    if (url) window.location.href = url;
  }, []);

  const loginGoogle = useCallback(() => {
    setDialogOpen(false);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + '/';
    window.location.href =
      EMERGENT_AUTH_BASE +
      '/?redirect=' +
      encodeURIComponent(redirectUrl);
  }, []);

  const logout = useCallback(async () => {
    await postLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login: openLogin,
      loginTwitch,
      loginGoogle,
      openLogin,
      closeLogin,
      logout,
      refresh,
      dialogOpen
    }),
    [
      user,
      loading,
      openLogin,
      closeLogin,
      loginTwitch,
      loginGoogle,
      logout,
      refresh,
      dialogOpen
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
